import { useState } from 'react';
import PptxGenJS from 'pptxgenjs';
import { formatCurrency } from '@sailpoint/bva-core'
import { BENEFIT_VALUE_PROPOSITIONS } from '../libs/types';
import { imageUrlToBase64 } from '../libs/utils/image-utils';

import {
	FONT_FAMILY_NAME as poppinsFamily,
	//FONT_VARIANT_NAME as lightVariant,
	//FONT_FILENAME_FOR_VFS as lightFilename,
	//FONT_BASE64_DATA as poppinsLightBase64
} from '../libs/assets/fonts/Poppins-light.js';


export let results;

export let donutCanvasElement;
export let barCanvasElement;

//let cachedSailpointLogoSmallDataUrl =  null;
//const sailpointLogoAspectRatio = 0.355;

// --- PowerPoint Constants ---
const LAYOUT_WIDE_WIDTH_INCHES = 13.33;
const totalTableWidth = LAYOUT_WIDE_WIDTH_INCHES * 0.85;
const PPTX_COL_W_BENEFIT = 4.5;
const NUM_OTHER_COLUMNS = 7;
const remainingWidthForOthers = totalTableWidth - PPTX_COL_W_BENEFIT;
const PPTX_COL_W_OTHER_UNIFORM = remainingWidthForOthers / NUM_OTHER_COLUMNS;
const colors = ['#0033a1', '#0071CE', '#54C0E8', '#00B294', '#93D500', '#F2A900', '#FF5F00', '#E62E2E', '#8A3FFC', '#d60eb5', '#CC27B0', '#33B1FF', '#415364', '#718096', '#A0AEC0'];


const ExportPowerPointButton = ({ results, donutCanvasElement, barCanvasElement }) => {
	const [isExporting, setIsExporting] = useState(false);

	async function handleExportPowerPoint() {
		if (!results) {
			console.log('PowerPoint export requires calculation results.');
			return;
		}

		console.log('Starting PowerPoint export...');
		setIsExporting(true);

		try {
			console.log('PowerPoint export: Loading assets and canvases...');
			// -cachedSailpointLogoSmallDataUrl-- Load Assets and Canvases ---
			//if (!) {
			//console.log('Loading SailPoint logo for PowerPoint export...');
			//const logoDataUrl = await imageUrlToBase64('/sailpoint.png');
			//if (!logoDataUrl) throw new Error('Failed to load SailPoint logo.');
			//	cachedSailpointLogoSmallDataUrl = logoDataUrl;
			//}
			if (!donutCanvasElement || !barCanvasElement) {
				console.log('Chart canvases not found or not ready for PowerPoint export.');
				throw new Error('Chart canvases not found or not ready.');
			}
			const donutImageDataUrl = donutCanvasElement.toDataURL('image/png');
			const barImageDataUrl = barCanvasElement.toDataURL('image/png');

			console.log('PowerPoint export: Creating presentation...');
			const pptx = new PptxGenJS();
			pptx.layout = 'LAYOUT_WIDE';
			// Use Poppins fontFace (must be installed on system for correct rendering)

			// --- Slide 1: Title Slide ---
			let slide1 = pptx.addSlide({ background: { color: 'FFFFFF' } });
			slide1.addShape(pptx.ShapeType.rect, {
				x: 0,
				y: 0,
				w: '100%',
				h: '100%',
				fill: { color: '0A209D' },
				line: 'none'
			});
			// Add PNG logo to top-right, padded by 20px
			const slideWidthPx = 960;
			const imageWidthPx = 600;
			const imageHeightPx = 200;
			const paddingPx = 20;
			const logoPngDataUrl = await imageUrlToBase64('/SailPoint-Logo-RGB-Inverse.png');
			if (logoPngDataUrl) {
				// Center the image horizontally, keep vertical position, use actual size
				slide1.addImage({
					data: logoPngDataUrl,
					x: ((slideWidthPx - (imageWidthPx / 2))) / 96, // center horizontally
					y: paddingPx / 96, // keep vertical position
					w: imageWidthPx / 96, // actual width in inches
					h: imageHeightPx / 96 // actual height in inches
				});
			}
			//			if (cachedSailpointLogoSmallDataUrl) {
			//				slide1.addImage({
			//					data: cachedSailpointLogoSmallDataUrl,
			//					x: 0.35, y: 2.5, w: 4.0, h: 4.0 * sailpointLogoAspectRatio
			//				});
			//			}
			slide1.addText('Benefits Analysis', {
				x: 0.75, y: 3.9, w: 8.0, h: 0.8,
				fontFace: "Poppins",
				fontSize: 40, color: 'FFFFFF', bold: true
			});



			// --- Slide 2: Combined Charts Slide ---
			let slide2 = pptx.addSlide({ background: { color: 'FFFFFF' } });
			slide2.addText('Estimated 5 Year Benefit Breakdown', {
				x: 0.5, y: 0.5, w: '90%', h: 0.5,
				fontSize: 36, color: '0033A1', fontFace: "Poppins", bold: true
			});

			const CHART_TITLE_Y = 1.5;
			const CHART_TITLE_H = 0.4;
			const CHART_BLOCK_Y = CHART_TITLE_Y + CHART_TITLE_H + 0.3;
			const H_SPACING_BETWEEN_BLOCKS = 0.8;
			const DONUT_CHART_W = 3.5;
			const DONUT_LEGEND_W = 2.5;
			const BAR_CHART_W = 6.0;

			const donutAspectRatio = donutCanvasElement.height / donutCanvasElement.width;
			const donutCalculatedHeight = DONUT_CHART_W * donutAspectRatio;
			const barAspectRatio = barCanvasElement.height / barCanvasElement.width;
			const barCalculatedHeight = BAR_CHART_W * barAspectRatio;
			const maxChartHeight = Math.max(donutCalculatedHeight, barCalculatedHeight);

			const DONUT_BLOCK_W = DONUT_CHART_W + 0.2 + DONUT_LEGEND_W;
			const TOTAL_CONTENT_W = DONUT_BLOCK_W + H_SPACING_BETWEEN_BLOCKS + BAR_CHART_W;
			const CONTENT_START_X = (LAYOUT_WIDE_WIDTH_INCHES - TOTAL_CONTENT_W) / 2;
			const DONUT_BLOCK_X = CONTENT_START_X;
			const BAR_BLOCK_X = DONUT_BLOCK_X + DONUT_BLOCK_W + H_SPACING_BETWEEN_BLOCKS;
			const DONUT_CHART_IMAGE_X = DONUT_BLOCK_X;
			const DONUT_LEGEND_X = DONUT_CHART_IMAGE_X + DONUT_CHART_W + 0.2;

			slide2.addText('5-Year Savings Breakdown', {
				x: DONUT_BLOCK_X, y: CHART_TITLE_Y, w: DONUT_BLOCK_W, h: CHART_TITLE_H,
				fontSize: 18, color: '415364', fontFace: "Poppins", bold: true, align: 'center'
			});
			slide2.addText('Total Savings per Year', {
				x: BAR_BLOCK_X, y: CHART_TITLE_Y, w: BAR_CHART_W, h: CHART_TITLE_H,
				fontSize: 18, color: '415364', fontFace: "Poppins", bold: true, align: 'center'
			});

			const donutImageY = CHART_BLOCK_Y + (maxChartHeight - donutCalculatedHeight) / 2;
			slide2.addImage({
				data: donutImageDataUrl, x: DONUT_CHART_IMAGE_X, y: donutImageY,
				w: DONUT_CHART_W, h: donutCalculatedHeight
			});

			const legendItemHeight = 0.2;
			const colorSwatchSize = 0.1;
			const textOffsetFromSwatch = 0.2;
			const totalLegendHeight = results.donutChartData.length * legendItemHeight;
			let legendStartY = CHART_BLOCK_Y + (maxChartHeight - totalLegendHeight) / 2;
			results.donutChartData.forEach((item, index) => {
				const colorHex = colors[index % colors.length];
				slide2.addShape(pptx.ShapeType.rect, {
					x: DONUT_LEGEND_X, y: legendStartY + (legendItemHeight - colorSwatchSize) / 2,
					w: colorSwatchSize, h: colorSwatchSize, fill: { color: colorHex }
				});
				slide2.addText(item.benefit, {
					x: DONUT_LEGEND_X + textOffsetFromSwatch, y: legendStartY,
					w: DONUT_LEGEND_W - textOffsetFromSwatch, h: legendItemHeight,
					fontFace: poppinsFamily,
					fontSize: 9,
					color: '415364', valign: 'middle'
				});
				legendStartY += legendItemHeight;
			});

			const barImageY = CHART_BLOCK_Y + (maxChartHeight - barCalculatedHeight) / 2;
			slide2.addImage({
				data: barImageDataUrl, x: BAR_BLOCK_X, y: barImageY,
				w: BAR_CHART_W, h: barCalculatedHeight
			});

			// --- Benefit Table Pagination Logic ---
			const sortedPositiveBenefits = results.savingsBreakdown
				.filter((row) => row.total > 0 && row.benefit !== 'Total')
				.sort((a, b) => b.total - a.total);

			let benefitChunks = [];
			const totalBenefits = sortedPositiveBenefits.length;
			const MAX_BENEFITS_PER_SLIDE = 8;
			const MIN_BENEFITS_ON_LAST_SLIDE = 3;

			if (totalBenefits <= MAX_BENEFITS_PER_SLIDE) {
				if (totalBenefits > 0) {
					benefitChunks.push(sortedPositiveBenefits);
				}
			} else {
				const remainder = totalBenefits % MAX_BENEFITS_PER_SLIDE;
				const numPages = Math.ceil(totalBenefits / MAX_BENEFITS_PER_SLIDE);

				if (numPages === 2 && remainder > 0 && remainder < MIN_BENEFITS_ON_LAST_SLIDE) {
					const splitPoint = Math.ceil(totalBenefits / 2);
					benefitChunks.push(sortedPositiveBenefits.slice(0, splitPoint));
					benefitChunks.push(sortedPositiveBenefits.slice(splitPoint));
				} else {
					for (let i = 0; i < totalBenefits; i += MAX_BENEFITS_PER_SLIDE) {
						benefitChunks.push(sortedPositiveBenefits.slice(i, i + MAX_BENEFITS_PER_SLIDE));
					}
				}
			}

			// --- Loop through chunks and create slides ---
			if (benefitChunks.length > 0) {
				const benefitTableHead = [[
					{ text: 'Benefit', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
					{ text: 'How Does SailPoint add Value', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
					{ text: 'Total', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle', align: 'center' } }
				]];

				benefitChunks.forEach((chunk, index) => {
					let benefitSlide = pptx.addSlide({ background: { color: 'FFFFFF' } });

					let slideTitle = 'Key Value Drivers for Identity Security Cloud';
					if (benefitChunks.length > 1) {
						slideTitle += ` (${index + 1}/${benefitChunks.length})`;
					}

					benefitSlide.addText(slideTitle, { x: 0.5, y: 0.5, w: '90%', h: 0.5, fontSize: 36, color: '0033A1', fontFace: poppinsFamily, bold: true });

					const benefitTableBody = chunk.map((row) => {
						const benefitKey = row.benefit || 'Optimize User Productivity';
						const rawValue = BENEFIT_VALUE_PROPOSITIONS[benefitKey];
						const valuePropText = (typeof rawValue === 'string' ? rawValue : 'N/A')
							.split('.')
							.filter((s) => s.trim() !== '')
							.map((s) => `• ${s.trim()}`)
							.join('\n');
						// Wrap the total value in an object to force center alignment
						return [row.benefit, valuePropText, { text: formatCurrency(row.total, results.currencySymbol, 'en-US', true), options: { align: 'center' } }];
					});

					const benefitTableRows = [...benefitTableHead, ...benefitTableBody];

					// Set column widths: Benefit (2.5), Value (8.5), Total (2.0)
					// Render the table and manually add a line shape under the header
					const tableY = 1.5;
					const tableX = 0.6;
					const tableW = LAYOUT_WIDE_WIDTH_INCHES - 1.2; // Medium width for balanced fit
					const colW = [2.35, 7.7, 1.95];
					benefitSlide.addTable(benefitTableRows, {
						x: tableX, y: tableY, w: tableW,
						colW,
						autoPage: true,
						valign: 'top', fontFace: poppinsFamily, fontSize: 10,
						border: [false, false, false, false],
						vert: false,
						rowStyles: {
							0: { color: '111827', bold: true, fontSize: 11, valign: 'middle', colAlign: ['left', 'left', 'center'] }
						},
						colStyles: {
							0: { align: 'left', valign: 'middle' },
							1: { align: 'left', valign: 'middle' },
							2: { align: 'center', valign: 'middle' }
						},
						rowBorder: (rowIdx, rowCount) => {
							// Only dotted lines for body rows
							if (rowIdx > 0) {
								return { bottom: { style: 'dotted', color: 'E5E7EB', pt: 1 } };
							} else {
								return {};
							}
						},
						cellPadding: 8
					});
				});
			}

			// --- Detailed Table Slide ---
			let detailedTableSlide = pptx.addSlide({ background: { color: 'FFFFFF' } });
			detailedTableSlide.addText('Detailed Savings Breakdown', {
				x: 0.5, y: 0.5, w: '90%', h: 0.5,
				fontSize: 36, color: '0033A1', fontFace: poppinsFamily, bold: true
			});

			const detailedHead = [[
				{ text: 'Benefit', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Year 1', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Year 2', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Year 3', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Year 4', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Year 5', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: 'Total', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } },
				{ text: '% Improvement', options: { fill: 'F3F4F6', color: '111827', bold: true, fontSize: 11, valign: 'middle' } }
			]];
			const detailedBody = results.savingsBreakdown
				.filter((row) => row.total > 0 || row.benefit === 'Total')
				.sort((a, b) => {
					if (a.benefit === 'Total') return 1;
					if (b.benefit === 'Total') return -1;
					return b.total - a.total;
				})
				.map((row) => [
					row.benefit,
					formatCurrency(row.y1, results.currencySymbol),
					formatCurrency(row.y2, results.currencySymbol),
					formatCurrency(row.y3, results.currencySymbol),
					formatCurrency(row.y4, results.currencySymbol),
					formatCurrency(row.y5, results.currencySymbol),
					formatCurrency(row.total, results.currencySymbol),
					row.benefit !== 'Total' ? `${row.percentageImprovement}%` : '-'
				]);
			const detailedTableRows = [...detailedHead, ...detailedBody];

			// Adjust column widths: make '% Improvement' about 3 chars wider
			// Increase the width of the last column ('% Improvement') by 0.2 and reduce others slightly
			const totalColExtra = -0.1; // Reduce by another 0.1 (now negative)
			const percentColExtra = 0.4;
			const benefitColReduced = PPTX_COL_W_BENEFIT - totalColExtra - percentColExtra;
			const adjustedOther = PPTX_COL_W_OTHER_UNIFORM;
			const detailedColWidths = [
				benefitColReduced, // Benefit
				adjustedOther,     // Year 1
				adjustedOther,     // Year 2
				adjustedOther,     // Year 3
				adjustedOther,     // Year 4
				adjustedOther,     // Year 5
				PPTX_COL_W_OTHER_UNIFORM + 0.3 + totalColExtra, // Total (index 6)
				PPTX_COL_W_OTHER_UNIFORM + percentColExtra      // % Improvement (index 7)
			];
			detailedTableSlide.addTable(detailedTableRows, {
				x: (LAYOUT_WIDE_WIDTH_INCHES - totalTableWidth) / 2,
				y: 1.5,
				w: totalTableWidth,
				colW: detailedColWidths,
				rowStyles: {
					0: { bold: true, fontSize: 11, valign: 'middle' }
				},
				colStyles: {
					0: { align: 'left', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					1: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					2: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					3: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					4: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					5: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					6: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' },
					7: { align: 'right', valign: 'middle', fill: 'F3F4F6', color: '111827' }
				},
				rowBorder: { style: 'dotted', color: 'E5E7EB', pt: 1 },
				cellPadding: 8,
				autoPage: true,
				fontSize: 10,
				fontFace: poppinsFamily
			});

			const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
			const filename = `BVA_Results_${timestamp}.pptx`;

			pptx.writeFile({ fileName: filename });

		} catch (err) {
			console.error('PowerPoint Export Error:', err);
		} finally {
			setIsExporting(false);
			setTimeout(() => { }, 5000);
		}
	}

	return (
		<>
			<button
				onClick={handleExportPowerPoint}
				disabled={isExporting || !results || !donutCanvasElement || !barCanvasElement}
				className="relative cursor-pointer px-6 py-2 w-3xs rounded-md shadow text-white font-semibold overflow-hidden bg-gradient-to-r from-blue-900 to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 group"
			>
				<span
					className="absolute inset-0 bg-gradient-to-r from-[#d60eb5] to-[#f06dff] opacity-0 transition-opacity duration-300 ease-in-out z-10 group-hover:opacity-100"
				></span>
				<span className="relative z-20">
					{isExporting ? 'Exporting...' : 'Export to PowerPoint'}
				</span>
			</button>
		</>
	);
};

export default ExportPowerPointButton;
